import { useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import {
  shopModeApi,
  type FloorScannedItem,
  type FloorSellableItem,
} from "../../api/shopMode";
import { BarcodeField } from "../BarcodeField";
import { useT } from "../../i18n/LanguageProvider";
import { categoryLabel } from "../../i18n/taxonomy";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";
import { eur, uomLabel } from "./shopFloorFormat";
import { compareText } from "../../i18n/intl";

/**
 * G-46 GAP 2 — the ONE way the floor finds stock, lifted out of
 * `ShopFloorSalePanel` so the till, the bouquet panel's inline sell and the
 * kiosk order form can never drift apart. All three finders in one place:
 * barcode scan, text search, category chips. The HOST owns the items list
 * (it already loads it) and the ticket; this component only finds and emits.
 */
export function StockItemFinder({
  items,
  loading,
  qtyInTicket,
  onPick,
}: {
  items: FloorSellableItem[];
  loading: boolean;
  /** item id → quantity already in the host's ticket (caps the picker). */
  qtyInTicket: Map<string, number>;
  onPick: (item: FloorSellableItem) => void;
}) {
  const { t } = useT();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [barcode, setBarcode] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanBusy, setScanBusy] = useState(false);
  /** G-61: the items sharing a scanned barcode, when there is more than one. */
  const [scanChoices, setScanChoices] = useState<FloorScannedItem[]>([]);

  const categories = useMemo(() => {
    const unique = new Map<string, FloorSellableItem["category"]>();
    for (const item of items) unique.set(item.category.code, item.category);
    return [...unique.values()].sort((a, b) =>
      compareText(categoryLabel(a.code, a.label), categoryLabel(b.code, b.label)),
    );
  }, [items]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    return items.filter(
      (item) =>
        (!categoryCode || item.category.code === categoryCode) &&
        (!query || item.name.toLocaleLowerCase().includes(query)),
    );
  }, [categoryCode, items, searchQuery]);

  /**
   * G-61: a barcode names a set, not one item (manufacturers reuse one EAN
   * across variants). One match adds straight to the ticket as before;
   * several are listed for the florist to tap.
   */
  const takeScannedItem = (scanned: FloorScannedItem): void => {
      // Do NOT clear the candidate list yet: this pick can still be rejected
      // (a fresh flower, nothing in stock here, already all in the ticket),
      // and taking the other candidates off the screen would force a rescan.
      setScanError(null);
      if (scanned.isPerishable) {
        setScanError(t("finder.perishable", { name: scanned.name }));
        return;
      }
      const stocked = items.find((item) => item.id === scanned.id);
      if (!stocked) {
        setScanError(t("finder.notInStock", { name: scanned.name }));
        return;
      }
      if ((qtyInTicket.get(stocked.id) ?? 0) >= Number(stocked.qtyOnHand)) {
        setScanError(t("finder.allInTicketNamed", { name: stocked.name }));
        return;
      }
      // Accepted — only now is the choice settled.
      setScanChoices([]);
      onPick(stocked);
      setBarcode("");
  };

  const addScannedItem = async (detectedCode?: string) => {
    const code = (detectedCode ?? barcode).trim();
    if (!code) return;
    setScanBusy(true);
    setScanError(null);
    setScanChoices([]);
    try {
      const scanned = await shopModeApi.floorFindItemsByBarcode(code);
      if (scanned.length > 1) {
        setScanChoices(scanned);
        return;
      }
      takeScannedItem(scanned[0]);
    } catch (error) {
      setScanError(
        errorText(error, "finder.barcodeUnknown"),
      );
    } finally {
      setScanBusy(false);
    }
  };

  return (
    <div className="sf-item-finder">
      <label htmlFor="sale-product-search" className="visually-hidden">
        {t("finder.searchLabel")}
      </label>
      <div className="shop-floor-sale-search-field">
        <ShopFloorIcon name="search" size={22} />
        <input
          id="sale-product-search"
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t("finder.searchPlaceholder")}
          autoComplete="off"
        />
        {searchQuery ? (
          <button
            type="button"
            className="shop-floor-sale-search-clear"
            onClick={() => setSearchQuery("")}
            aria-label={t("finder.clearSearch")}
          >
            ×
          </button>
        ) : null}
      </div>

      <div className="shop-floor-sale-category-tabs" aria-label={t("finder.categories")}>
        <button
          type="button"
          className={`shop-floor-sale-category${categoryCode === "" ? " is-active" : ""}`}
          onClick={() => setCategoryCode("")}
        >
          {t("finder.all")}
        </button>
        {categories.map((category) => (
          <button
            key={category.code}
            type="button"
            className={`shop-floor-sale-category${categoryCode === category.code ? " is-active" : ""}`}
            onClick={() => setCategoryCode(category.code)}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="shop-floor-sale-barcode-row">
        <div className="shop-floor-sale-barcode-field">
          <BarcodeField
            id="sale-barcode"
            value={barcode}
            onChange={setBarcode}
            label={t("finder.scanLabel")}
            onScanDetected={(code) => void addScannedItem(code)}
          />
        </div>
        <button
          type="button"
          className="button shop-floor-sale-barcode-add"
          onClick={() => void addScannedItem()}
          disabled={scanBusy || !barcode.trim()}
        >
          {scanBusy ? t("finder.lookingUp") : t("finder.add")}
        </button>
      </div>
      {scanChoices.length > 0 ? (
        <div className="stock-scan-choices">
          <p className="muted" style={{ margin: "0 0 var(--space-2)" }}>
            {t("finder.shareBarcode", { count: scanChoices.length })}
          </p>
          <div className="shop-floor-action-stack">
            {scanChoices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                className="button tinted"
                onClick={() => takeScannedItem(choice)}
              >
                {choice.name}
                {choice.sku ? ` · ${choice.sku}` : ""}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {scanError ? <div className="alert error">{scanError}</div> : null}

      {loading ? (
        <div className="empty-state">{t("finder.loadingProducts")}</div>
      ) : filteredItems.length === 0 ? (
        <div className="empty-state">
          {items.length === 0 ? t("finder.noProducts") : t("finder.noMatch")}
        </div>
      ) : (
        <div className="shop-floor-sale-product-grid">
          {filteredItems.map((item) => {
            const inTicket = qtyInTicket.get(item.id) ?? 0;
            const available = Number(item.qtyOnHand);
            const allInTicket = Number.isFinite(available) && inTicket >= available;
            return (
              <button
                key={item.id}
                type="button"
                className="shop-floor-sale-product-card"
                onClick={() => onPick(item)}
                disabled={allInTicket}
                aria-label={t("finder.addToTicket", { name: item.name })}
              >
                <div className="shop-floor-sale-product-photo">
                  <ShopFloorBouquetThumb
                    id={item.id}
                    imageKey={item.imageKey}
                    alt={item.name}
                  />
                  {inTicket > 0 ? (
                    <span className="shop-floor-sale-in-ticket">
                      {t("finder.inTicket", { count: inTicket })}
                    </span>
                  ) : null}
                </div>
                <div className="shop-floor-sale-product-copy">
                  <span className="shop-floor-sale-product-category">
                    {item.category.label}
                  </span>
                  <strong>{item.name}</strong>
                  <span className={item.saleUnitPriceEur == null ? "is-missing" : ""}>
                    {item.saleUnitPriceEur != null
                      ? t(
                          item.saleUnitPriceIsDerived
                            ? "finder.pricePerUnitDerived"
                            : "finder.pricePerUnit",
                          {
                            price: eur(Number(item.saleUnitPriceEur)),
                            unit: uomLabel(item.defaultUom, 1),
                          },
                        )
                      : t("finder.noPriceYet")}
                  </span>
                  <small>
                    {allInTicket
                      ? t("finder.allInTicket")
                      : t("finder.inStock", {
                          qty: item.qtyOnHand,
                          unit: uomLabel(item.defaultUom, available),
                        })}
                  </small>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
